import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, Loader2 } from "lucide-react";
import { loginUser } from "../services/auth";

/* ─── Logistics SVG Illustration ─── */
function LogisticsIllustration() {
  return (
    <svg
      viewBox="0 0 520 420"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full max-w-xl"
      aria-hidden="true"
    >
      {/* ── Ocean / Water ── */}
      <rect x="0" y="310" width="520" height="110" rx="0" fill="#0a2a4a" opacity="0.6" />
      <ellipse cx="260" cy="312" rx="260" ry="8" fill="#0d3b6e" opacity="0.5" />

      {/* Water ripple lines */}
      <path d="M20 330 Q80 325 140 330 Q200 335 260 330 Q320 325 380 330 Q440 335 500 330" stroke="#1a5276" strokeWidth="1.5" fill="none" opacity="0.7" />
      <path d="M40 345 Q100 340 160 345 Q220 350 280 345 Q340 340 400 345 Q460 350 500 345" stroke="#1a5276" strokeWidth="1.5" fill="none" opacity="0.5" />
      <path d="M10 360 Q70 355 130 360 Q190 365 250 360 Q310 355 370 360 Q430 365 510 360" stroke="#1a5276" strokeWidth="1" fill="none" opacity="0.4" />

      {/* ── Ship Hull ── */}
      <path d="M60 280 L460 280 L490 310 L30 310 Z" fill="#1a3a5c" />
      <path d="M60 280 L460 280 L490 310 L30 310 Z" stroke="#2e86c1" strokeWidth="1.5" fill="none" />
      {/* Hull stripe */}
      <path d="M35 298 L485 298" stroke="#e74c3c" strokeWidth="3" opacity="0.8" />

      {/* ── Ship Deck ── */}
      <rect x="80" y="260" width="360" height="22" rx="3" fill="#1b4f72" />

      {/* ── Containers Row 1 (bottom) ── */}
      {/* Container 1 - Red */}
      <rect x="90" y="210" width="72" height="52" rx="3" fill="#c0392b" />
      <rect x="90" y="210" width="72" height="52" rx="3" stroke="#96281b" strokeWidth="1.5" />
      <line x1="126" y1="210" x2="126" y2="262" stroke="#96281b" strokeWidth="1.5" />
      <line x1="90" y1="236" x2="162" y2="236" stroke="#96281b" strokeWidth="1.5" />
      {/* Door handles */}
      <rect x="120" y="228" width="4" height="8" rx="1" fill="#7b241c" />
      <rect x="128" y="228" width="4" height="8" rx="1" fill="#7b241c" />

      {/* Container 2 - Navy */}
      <rect x="168" y="210" width="72" height="52" rx="3" fill="#154360" />
      <rect x="168" y="210" width="72" height="52" rx="3" stroke="#1a5276" strokeWidth="1.5" />
      <line x1="204" y1="210" x2="204" y2="262" stroke="#1a5276" strokeWidth="1.5" />
      <line x1="168" y1="236" x2="240" y2="236" stroke="#1a5276" strokeWidth="1.5" />
      <rect x="198" y="228" width="4" height="8" rx="1" fill="#0e2f44" />
      <rect x="206" y="228" width="4" height="8" rx="1" fill="#0e2f44" />

      {/* Container 3 - Yellow/Amber */}
      <rect x="246" y="210" width="72" height="52" rx="3" fill="#d4a017" />
      <rect x="246" y="210" width="72" height="52" rx="3" stroke="#b7870b" strokeWidth="1.5" />
      <line x1="282" y1="210" x2="282" y2="262" stroke="#b7870b" strokeWidth="1.5" />
      <line x1="246" y1="236" x2="318" y2="236" stroke="#b7870b" strokeWidth="1.5" />
      <rect x="276" y="228" width="4" height="8" rx="1" fill="#9a6e09" />
      <rect x="284" y="228" width="4" height="8" rx="1" fill="#9a6e09" />

      {/* Container 4 - Dark Green */}
      <rect x="324" y="210" width="72" height="52" rx="3" fill="#1e6e3a" />
      <rect x="324" y="210" width="72" height="52" rx="3" stroke="#145a30" strokeWidth="1.5" />
      <line x1="360" y1="210" x2="360" y2="262" stroke="#145a30" strokeWidth="1.5" />
      <line x1="324" y1="236" x2="396" y2="236" stroke="#145a30" strokeWidth="1.5" />
      <rect x="354" y="228" width="4" height="8" rx="1" fill="#0d4620" />
      <rect x="362" y="228" width="4" height="8" rx="1" fill="#0d4620" />

      {/* ── Containers Row 2 (top) ── */}
      {/* Container A - Slate Blue */}
      <rect x="104" y="162" width="72" height="50" rx="3" fill="#2e5fa3" />
      <rect x="104" y="162" width="72" height="50" rx="3" stroke="#2554a0" strokeWidth="1.5" />
      <line x1="140" y1="162" x2="140" y2="212" stroke="#2554a0" strokeWidth="1.5" />
      <line x1="104" y1="187" x2="176" y2="187" stroke="#2554a0" strokeWidth="1.5" />
      <rect x="134" y="180" width="4" height="8" rx="1" fill="#1a3e7a" />
      <rect x="142" y="180" width="4" height="8" rx="1" fill="#1a3e7a" />

      {/* Container B - Orange */}
      <rect x="182" y="162" width="72" height="50" rx="3" fill="#c0541a" />
      <rect x="182" y="162" width="72" height="50" rx="3" stroke="#9a4112" strokeWidth="1.5" />
      <line x1="218" y1="162" x2="218" y2="212" stroke="#9a4112" strokeWidth="1.5" />
      <line x1="182" y1="187" x2="254" y2="187" stroke="#9a4112" strokeWidth="1.5" />
      <rect x="212" y="180" width="4" height="8" rx="1" fill="#7a3210" />
      <rect x="220" y="180" width="4" height="8" rx="1" fill="#7a3210" />

      {/* Container C - Steel */}
      <rect x="260" y="162" width="72" height="50" rx="3" fill="#4a6fa5" />
      <rect x="260" y="162" width="72" height="50" rx="3" stroke="#3a5a8a" strokeWidth="1.5" />
      <line x1="296" y1="162" x2="296" y2="212" stroke="#3a5a8a" strokeWidth="1.5" />
      <line x1="260" y1="187" x2="332" y2="187" stroke="#3a5a8a" strokeWidth="1.5" />
      <rect x="290" y="180" width="4" height="8" rx="1" fill="#2a4a7a" />
      <rect x="298" y="180" width="4" height="8" rx="1" fill="#2a4a7a" />

      {/* Container D - Crimson */}
      <rect x="338" y="162" width="72" height="50" rx="3" fill="#8e1a2e" />
      <rect x="338" y="162" width="72" height="50" rx="3" stroke="#6e1020" strokeWidth="1.5" />
      <line x1="374" y1="162" x2="374" y2="212" stroke="#6e1020" strokeWidth="1.5" />
      <line x1="338" y1="187" x2="410" y2="187" stroke="#6e1020" strokeWidth="1.5" />
      <rect x="368" y="180" width="4" height="8" rx="1" fill="#5a0e1a" />
      <rect x="376" y="180" width="4" height="8" rx="1" fill="#5a0e1a" />

      {/* ── Ship Superstructure (Bridge) ── */}
      <rect x="195" y="120" width="130" height="44" rx="4" fill="#1b4f72" />
      <rect x="195" y="120" width="130" height="44" rx="4" stroke="#2e86c1" strokeWidth="1.5" />
      {/* Bridge windows */}
      <rect x="210" y="130" width="18" height="12" rx="2" fill="#aed6f1" opacity="0.85" />
      <rect x="238" y="130" width="18" height="12" rx="2" fill="#aed6f1" opacity="0.85" />
      <rect x="266" y="130" width="18" height="12" rx="2" fill="#aed6f1" opacity="0.85" />
      <rect x="294" y="130" width="18" height="12" rx="2" fill="#aed6f1" opacity="0.85" />

      {/* Bridge top */}
      <rect x="210" y="106" width="100" height="16" rx="3" fill="#154360" />
      <rect x="210" y="106" width="100" height="16" rx="3" stroke="#2e86c1" strokeWidth="1" />

      {/* ── Chimney / Funnel ── */}
      <rect x="240" y="70" width="40" height="38" rx="4" fill="#1a252f" />
      <rect x="240" y="70" width="40" height="38" rx="4" stroke="#2c3e50" strokeWidth="1.5" />
      {/* Red band on funnel */}
      <rect x="240" y="82" width="40" height="10" fill="#c0392b" />
      {/* Smoke puffs */}
      <circle cx="255" cy="56" r="9" fill="#566573" opacity="0.5" />
      <circle cx="268" cy="46" r="7" fill="#717d7e" opacity="0.4" />
      <circle cx="278" cy="36" r="5" fill="#808b96" opacity="0.3" />
      <circle cx="262" cy="60" r="6" fill="#5d6d7e" opacity="0.35" />

      {/* ── Crane ── */}
      {/* Crane base */}
      <rect x="420" y="200" width="12" height="80" rx="2" fill="#2e4053" />
      {/* Crane arm */}
      <rect x="390" y="198" width="80" height="8" rx="2" fill="#2e4053" />
      {/* Crane cable */}
      <line x1="440" y1="206" x2="440" y2="240" stroke="#7f8c8d" strokeWidth="1.5" />
      {/* Crane hook */}
      <rect x="433" y="238" width="14" height="10" rx="2" fill="#566573" />
      {/* Small hanging container */}
      <rect x="430" y="248" width="20" height="15" rx="2" fill="#e74c3c" />
      <line x1="438" y1="248" x2="438" y2="263" stroke="#c0392b" strokeWidth="1" />
      {/* Crane tower detail */}
      <line x1="420" y1="240" x2="430" y2="200" stroke="#253040" strokeWidth="1.5" />
      <line x1="432" y1="240" x2="422" y2="200" stroke="#253040" strokeWidth="1.5" />

      {/* ── Small Tugboat ── */}
      <ellipse cx="60" cy="316" rx="38" ry="10" fill="#c0392b" />
      <rect x="26" y="305" width="68" height="12" rx="4" fill="#c0392b" />
      <rect x="36" y="293" width="36" height="14" rx="3" fill="#8e1a2e" />
      {/* Tug windows */}
      <rect x="41" y="297" width="8" height="5" rx="1" fill="#aed6f1" opacity="0.8" />
      <rect x="54" y="297" width="8" height="5" rx="1" fill="#aed6f1" opacity="0.8" />

      {/* ── Lighthouse (far right) ── */}
      <rect x="480" y="220" width="18" height="90" rx="3" fill="#ecf0f1" />
      <rect x="477" y="216" width="24" height="14" rx="2" fill="#e74c3c" />
      {/* Light beam hint */}
      <ellipse cx="489" cy="216" rx="5" ry="4" fill="white" opacity="0.9" />
      {/* Red stripes on lighthouse */}
      <rect x="480" y="240" width="18" height="10" fill="#e74c3c" opacity="0.8" />
      <rect x="480" y="270" width="18" height="10" fill="#e74c3c" opacity="0.8" />
      <rect x="480" y="300" width="18" height="10" fill="#e74c3c" opacity="0.8" />
      {/* Lighthouse base */}
      <rect x="474" y="308" width="30" height="8" rx="2" fill="#bdc3c7" />

      {/* ── Stars / dots in sky ── */}
      {[
        [30, 30],[80, 15],[150, 40],[350, 20],[420, 10],[460, 35],[500, 25],
        [10, 70],[70, 80],[200, 10],[300, 50],[380, 30],[500, 60]
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="1.5" fill="white" opacity="0.5" />
      ))}

      {/* ── Horizon glow ── */}
      <ellipse cx="260" cy="305" rx="260" ry="12" fill="#1a5276" opacity="0.3" />
    </svg>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await loginUser(form.email, form.password);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      navigate("/");
    } catch (err) {
      setError(err?.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ fontFamily: "'Barlow', 'Segoe UI', sans-serif" }}
    >
      {/* ─── LEFT PANEL ─── */}
      <div className="hidden lg:flex lg:w-[58%] relative overflow-hidden flex-col">
        {/* Deep navy gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(160deg, #0a1628 0%, #0d2748 40%, #0f3460 70%, #0a1e3d 100%)",
          }}
        />

        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(46,134,193,0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(46,134,193,0.4) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Top accent bar */}
        <div
          className="absolute top-0 left-0 right-0 h-1"
          style={{ background: "linear-gradient(90deg, #C8102E, #e74c3c, #C8102E)" }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-10 text-white">
          {/* Logo / Branding */}
          <div className="flex items-center gap-4 mb-auto">
            <div
              className="flex items-center justify-center rounded-xl"
              style={{
                width: 52,
                height: 52,
                background: "linear-gradient(135deg, #C8102E 0%, #a00d25 100%)",
                boxShadow: "0 4px 20px rgba(200,16,46,0.4)",
              }}
            >
              {/* Anchor icon */}
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="5" r="3" />
                <line x1="12" y1="22" x2="12" y2="8" />
                <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
              </svg>
            </div>
            <div>
              <p
                className="font-bold tracking-widest uppercase text-sm"
                style={{ color: "#C8102E", letterSpacing: "0.2em" }}
              >
                Meraki
              </p>
              <p className="text-base font-semibold text-white leading-tight">
                Logistics Admin Portal
              </p>
            </div>
          </div>

          {/* Illustration */}
          <div className="flex justify-center items-center py-4 -mx-4">
            <LogisticsIllustration />
          </div>

          {/* Heading */}
          <div className="mb-6">
            <h2
              className="text-4xl font-bold leading-tight"
              style={{ fontFamily: "'Barlow Condensed', 'Barlow', sans-serif", letterSpacing: "-0.01em" }}
            >
              The World's Most Trusted
              <br />
              <span style={{ color: "#2e86c1" }}>Container & Freight</span>
              <br />
              Management Platform
            </h2>
            <p className="mt-3 text-sm leading-6" style={{ color: "rgba(255,255,255,0.55)", maxWidth: 420 }}>
              Operate your ports, containers, invoices, and freight routes from a single
              secure control centre — built for logistics professionals worldwide.
            </p>
          </div>

          {/* Bottom feature badges */}
          <div className="flex gap-3 flex-wrap">
            {[
              { icon: "🚢", label: "Port Operations" },
              { icon: "📦", label: "Container Tracking" },
              { icon: "🧾", label: "Invoice Management" },
              { icon: "🔒", label: "Secure Access" },
            ].map(({ icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
                style={{
                  background: "rgba(46,134,193,0.12)",
                  border: "1px solid rgba(46,134,193,0.25)",
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                <span>{icon}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── RIGHT PANEL ─── */}
      <div
        className="flex w-full lg:w-[42%] items-center justify-center px-6 py-12 relative"
        style={{ background: "#f5f7fa" }}
      >
        {/* Subtle top red line on mobile */}
        <div
          className="absolute top-0 left-0 right-0 h-1 lg:hidden"
          style={{ background: "linear-gradient(90deg, #C8102E, #e74c3c)" }}
        />

        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="mb-8 lg:hidden text-center">
            <div
              className="inline-flex items-center justify-center rounded-xl mb-3"
              style={{
                width: 52,
                height: 52,
                background: "linear-gradient(135deg, #C8102E, #a00d25)",
              }}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="5" r="3" />
                <line x1="12" y1="22" x2="12" y2="8" />
                <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold" style={{ color: "#0a1628" }}>Meraki Logistics Admin</h1>
            <p className="text-sm mt-1" style={{ color: "#6b7280" }}>Sign in to continue</p>
          </div>

          {/* Card */}
          <div
            className="rounded-2xl p-8"
            style={{
              background: "#ffffff",
              boxShadow: "0 4px 40px rgba(10,22,40,0.1), 0 1px 4px rgba(10,22,40,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            {/* Card header */}
            <div className="mb-7">
              <div className="flex items-center gap-2 mb-2">
                <div style={{ width: 28, height: 3, background: "#C8102E", borderRadius: 2 }} />
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "#C8102E" }}>
                  Secure Login
                </p>
              </div>
              <h2 className="text-2xl font-bold" style={{ color: "#0a1628", letterSpacing: "-0.02em" }}>
                Welcome back
              </h2>
              <p className="mt-1 text-sm" style={{ color: "#6b7280" }}>
                Sign in with your admin credentials to access the portal.
              </p>
            </div>

            {/* Error */}
            {error && (
              <div
                className="mb-5 rounded-xl px-4 py-3 text-sm"
                style={{
                  background: "#fff5f5",
                  border: "1px solid #fca5a5",
                  color: "#b91c1c",
                }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label
                  className="block text-sm font-semibold mb-2"
                  style={{ color: "#374151" }}
                >
                  Email Address
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-3.5 top-1/2 -translate-y-1/2"
                    style={{ width: 16, height: 16, color: "#9ca3af" }}
                  />
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full rounded-xl pl-10 pr-4 py-3 text-sm outline-none transition-all"
                    style={{
                      border: "1.5px solid #e5e7eb",
                      color: "#111827",
                      background: "#fafafa",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#0d2748";
                      e.target.style.boxShadow = "0 0 0 3px rgba(13,39,72,0.08)";
                      e.target.style.background = "#fff";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.boxShadow = "none";
                      e.target.style.background = "#fafafa";
                    }}
                    placeholder="admin@example.com"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label
                  className="block text-sm font-semibold mb-2"
                  style={{ color: "#374151" }}
                >
                  Password
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-3.5 top-1/2 -translate-y-1/2"
                    style={{ width: 16, height: 16, color: "#9ca3af" }}
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    className="w-full rounded-xl pl-10 pr-12 py-3 text-sm outline-none transition-all"
                    style={{
                      border: "1.5px solid #e5e7eb",
                      color: "#111827",
                      background: "#fafafa",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#0d2748";
                      e.target.style.boxShadow = "0 0 0 3px rgba(13,39,72,0.08)";
                      e.target.style.background = "#fff";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.boxShadow = "none";
                      e.target.style.background = "#fafafa";
                    }}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: "#9ca3af" }}
                    onMouseOver={(e) => (e.currentTarget.style.color = "#374151")}
                    onMouseOut={(e) => (e.currentTarget.style.color = "#9ca3af")}
                  >
                    {showPassword ? (
                      <EyeOff style={{ width: 16, height: 16 }} />
                    ) : (
                      <Eye style={{ width: 16, height: 16 }} />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember me + Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="rounded"
                    style={{ accentColor: "#0d2748", width: 15, height: 15 }}
                  />
                  <span className="text-sm" style={{ color: "#6b7280" }}>
                    Remember me
                  </span>
                </label>
                <Link
                  to="#"
                  className="text-sm font-semibold transition-colors"
                  style={{ color: "#0d2748" }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "#C8102E")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "#0d2748")}
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold tracking-wide transition-all"
                style={{
                  background: loading
                    ? "#6b7280"
                    : "linear-gradient(135deg, #0d2748 0%, #1a4a8a 100%)",
                  color: "white",
                  boxShadow: loading ? "none" : "0 4px 16px rgba(13,39,72,0.35)",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.8 : 1,
                  letterSpacing: "0.04em",
                }}
                onMouseOver={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #C8102E 0%, #a00d25 100%)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(200,16,46,0.4)";
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0d2748 0%, #1a4a8a 100%)";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(13,39,72,0.35)";
                  }
                }}
              >
                {loading ? (
                  <>
                    <Loader2 style={{ width: 18, height: 18 }} className="animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  "Sign In to Portal"
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p className="mt-6 text-center text-xs" style={{ color: "#9ca3af" }}>
            © 2026 Meraki Logistics Admin · All rights reserved ·{" "}
            <Link to="#" style={{ color: "#6b7280" }} className="hover:underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
